import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import { strengthReportService, generateStrengthReportHTML } from '../../services/strengthReportService';
import { patientService } from '../../services/patientService';
import { format } from 'date-fns';
import { requestPDF } from '../../services/pdfService';
import PublishingOverlay from '../common/PublishingOverlay';
import { generatePDFFilename } from '../../utils/generatePDFFilename';

const StrengthReportForm = () => {
  const navigate = useNavigate();
  const { id, patientId } = useParams();
  const appointmentId = patientId;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [publishingStatus, setPublishingStatus] = useState(null);
  const [patient, setPatient] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  const [formData, setFormData] = useState({
    patient_id: '',
    patient_name: '',
    patient_gender: '',
    patient_age: '',
    exam_date: format(new Date(), 'yyyy-MM-dd'),
    
    // Upper body exercises
    upper_body: [
      { name: 'Shoulder Abduction', right: '', left: '', asymmetry: '', description: '' },
      { name: 'Elbow Flexion', right: '', left: '', asymmetry: '', description: '' },
      { name: 'Elbow Extension', right: '', left: '', asymmetry: '', description: '' },
      { name: 'Chest - Force plate push ups', right: '', left: '', asymmetry: '', description: '' },
      { name: 'Hand Grip Squeeze', right: '', left: '', asymmetry: '', description: '' }
    ],
    
    // Lower body exercises
    lower_body: [
      { name: 'Hip Flexion', right: '', left: '', asymmetry: '', description: '' },
      { name: 'Hip Extension', right: '', left: '', asymmetry: '', description: '' },
      { name: 'Knee Extension', right: '', left: '', asymmetry: '', description: '' },
      { name: 'Ankle Dorsiflexion', right: '', left: '', asymmetry: '', description: '' }
    ],
    
    // Suggestions
    suggestions: [{ title: '', description: '' }],
    
    notes: '',
    recommendations: '',
    pdf_url: ''
  });

  const sections = [
    {
      label: 'Patient Information',
      fields: [
        { name: 'patient_name', label: 'Patient Name', type: 'text', required: true },
        { name: 'patient_age', label: 'Age', type: 'number', required: true },
        { name: 'patient_gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'], required: true },
        { name: 'exam_date', label: 'Exam Date', type: 'date', required: true }
      ]
    },
    {
      label: 'Upper Body',
      component: 'upper_body'
    },
    {
      label: 'Lower Body',
      component: 'lower_body'
    },
    {
      label: 'Suggestions',
      component: 'suggestions'
    }
  ];

  // Helper function to calculate age from DOB
  const calculateAgeFromDOB = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to normalize gender
  const normalizeGender = (gender) => {
    if (!gender) return '';
    const genderLower = gender.toLowerCase().trim();
    if (genderLower === 'male' || genderLower === 'm') return 'Male';
    if (genderLower === 'female' || genderLower === 'f') return 'Female';
    if (genderLower === 'other') return 'Other';
    return gender;
  };

  // Load patient data
  useEffect(() => {
    const loadPatientData = async () => {
      try {
        if (appointmentId) {
          console.log('Loading patient data for appointmentId:', appointmentId);
          const response = await patientService.getPatientById(appointmentId);
          console.log('Patient data response:', response);
          
          const patientData = response.data?.data || response.data || response;
          console.log('Extracted patient data:', patientData);
          
          setPatient(patientData);
          
          // Extract and process patient information
          const profileData = patientData.profile || {};
          const patientName = profileData.name || '';
          const patientGender = normalizeGender(profileData.gender);
          
          let patientAge = profileData.age;
          if (!patientAge && profileData.dob) {
            patientAge = calculateAgeFromDOB(profileData.dob);
          }
          
          setFormData(prev => ({
            ...prev,
            patient_id: patientData.id || '',
            patient_name: patientName,
            patient_gender: patientGender,
            patient_age: patientAge || ''
          }));
          
          // Fetch existing strength report data by appointment ID
          try {
            console.log('Attempting to fetch existing strength report for appointmentId:', appointmentId);
            const reportData = await strengthReportService.getReportByAppointmentId(appointmentId);
            console.log('Report data response:', reportData);
            
            if (reportData && reportData.data) {
              setReportId(reportData.data.id);
              setIsEditMode(true);
              console.log('Existing report found, setting edit mode with reportId:', reportData.data.id);
              
              // Transform API response to form data structure
              const apiData = reportData.data;
              const transformedData = {
                patient_id: apiData.patient_id || '',
                patient_name: apiData.patient_name || '',
                patient_gender: apiData.patient_gender || '',
                patient_age: apiData.patient_age || '',
                exam_date: apiData.exam_date || '',
                
                // Upper body exercises
                upper_body: [
                  apiData.shoulder_abduction || { name: 'Shoulder Abduction', right: '', left: '', asymmetry: '', description: '' },
                  apiData.elbow_flexion || { name: 'Elbow Flexion', right: '', left: '', asymmetry: '', description: '' },
                  apiData.elbow_extension || { name: 'Elbow Extension', right: '', left: '', asymmetry: '', description: '' },
                  apiData.chest_force_plate_push_ups || { name: 'Chest - Force plate push ups', right: '', left: '', asymmetry: '', description: '' },
                  apiData.hand_grip_squeeze || { name: 'Hand Grip Squeeze', right: '', left: '', asymmetry: '', description: '' }
                ],
                
                // Lower body exercises
                lower_body: [
                  apiData.hip_flexion || { name: 'Hip Flexion', right: '', left: '', asymmetry: '', description: '' },
                  apiData.hip_extension || { name: 'Hip Extension', right: '', left: '', asymmetry: '', description: '' },
                  apiData.knee_extension || { name: 'Knee Extension', right: '', left: '', asymmetry: '', description: '' },
                  apiData.ankle_dorsiflexion || { name: 'Ankle Dorsiflexion', right: '', left: '', asymmetry: '', description: '' }
                ],
                
                // Suggestions
                suggestions: apiData.suggestions && apiData.suggestions.length > 0 
                  ? apiData.suggestions 
                  : [{ title: '', description: '' }],
                
                notes: apiData.notes || '',
                recommendations: apiData.recommendations || '',
                pdf_url: apiData.pdf_url || ''
              };
              
              setFormData(prev => ({
                ...prev,
                ...transformedData
              }));
            } else {
              console.log('No report data found in response');
              setIsEditMode(false);
            }
          } catch (reportError) {
            console.log('No existing strength report found or error fetching:', reportError.message);
            setIsEditMode(false);
          }
        } else {
          console.log('No appointmentId provided');
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load patient data');
      } finally {
        setLoading(false);
      }
    };

    loadPatientData();
  }, [appointmentId]);

  const handleExerciseChange = (section, index, field, value) => {
    setFormData(prev => {
      const exercises = [...prev[section]];
      exercises[index] = {
        ...exercises[index],
        [field]: value
      };
      return {
        ...prev,
        [section]: exercises
      };
    });
  };

  const handleSuggestionChange = (index, field, value) => {
    setFormData(prev => {
      const suggestions = [...prev.suggestions];
      suggestions[index] = {
        ...suggestions[index],
        [field]: value
      };
      return {
        ...prev,
        suggestions
      };
    });
  };

  const addSuggestion = () => {
    setFormData(prev => ({
      ...prev,
      suggestions: [...prev.suggestions, { title: '', description: '' }]
    }));
  };

  const removeSuggestion = (index) => {
    setFormData(prev => ({
      ...prev,
      suggestions: prev.suggestions.filter((_, i) => i !== index)
    }));
  };

  const renderPatientInfoSection = () => {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Patient Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.patient_name || ''}
              onChange={(e) => handleFormChange('patient_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter patient name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Age <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.patient_age || ''}
              onChange={(e) => handleFormChange('patient_age', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter age"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.patient_gender || ''}
              onChange={(e) => handleFormChange('patient_gender', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Exam Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.exam_date || ''}
              onChange={(e) => handleFormChange('exam_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
      </div>
    );
  };

  const calculateAsymmetry = (right, left) => {
    if (!right || !left) return '';
    
    const rightVal = parseFloat(right);
    const leftVal = parseFloat(left);
    
    if (isNaN(rightVal) || isNaN(leftVal)) return '';
    
    const max = Math.max(rightVal, leftVal);
    const min = Math.min(rightVal, leftVal);
    
    if (max === 0) return '';
    
    const asymmetry = ((max - min) / max * 100).toFixed(1);
    const side = rightVal > leftVal ? 'Right' : 'Left';
    
    return `${asymmetry}% ${side}`;
  };

  const renderExerciseSection = (section, title) => {
    const exercises = formData[section];

    return (
      <div className="space-y-4">
        {exercises.map((exercise, index) => {
          const calculatedAsymmetry = calculateAsymmetry(exercise.right, exercise.left);
          
          return (
            <div key={index} className="p-4 border border-gray-200 rounded bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {exercise.name || `Exercise ${index + 1}`}
              </h3>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Right (kg)
                  </label>
                  <input
                    type="text"
                    value={exercise.right || ''}
                    onChange={(e) => handleExerciseChange(section, index, 'right', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="e.g., 7.5"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Left (kg)
                  </label>
                  <input
                    type="text"
                    value={exercise.left || ''}
                    onChange={(e) => handleExerciseChange(section, index, 'left', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="e.g., 8.7"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Asymmetry
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white text-gray-700">
                    {calculatedAsymmetry || '-'}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSuggestionsSection = () => {
    return (
      <div className="space-y-3">
        {formData.suggestions.map((suggestion, index) => (
          <div key={index} className="p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-xs font-medium text-gray-700">
                Suggestion {index + 1}
              </label>
              {formData.suggestions.length > 1 && (
                <button
                  onClick={() => removeSuggestion(index)}
                  className="text-red-500 hover:text-red-700 text-xs"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={suggestion.title || ''}
                onChange={(e) => handleSuggestionChange(index, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                placeholder="Enter suggestion title"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={suggestion.description || ''}
                onChange={(e) => handleSuggestionChange(index, 'description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                rows="2"
                placeholder="Enter suggestion description"
              />
            </div>
          </div>
        ))}

        <Button
          variant="secondary"
          onClick={addSuggestion}
          className="w-full text-sm"
        >
          Add Another Suggestion
        </Button>
      </div>
    );
  };

  const handlePreview = async () => {
    try {
      const html = await generateStrengthReportHTML(formData);
      sessionStorage.setItem('strengthReportData', JSON.stringify(formData));
      
      const previewUrl = `/strength-report/${appointmentId}/preview`;
      window.open(previewUrl, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    }
  };

  const handleSubmit = async (status) => {
    setSubmitting(true);
    let pdf_url = null;
    
    try {
      if (status === 'published') {
        setPublishingStatus('generating');
        try {
          const html = await generateStrengthReportHTML(formData);
          const pdfBlob = await requestPDF(html);
          const pdfObjectUrl = URL.createObjectURL(pdfBlob);
          
          const patientName = patient?.profile?.name || formData.patient_name || patient?.name || 'patient';
          const orderId = patient?.sukra_order_id || patient?.id || 'unknown';
          const fileName = generatePDFFilename(patientName, 'strength', orderId);
          console.log('Uploading PDF with filename:', fileName);
          
          try {
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            const uploadFormData = new FormData();
            uploadFormData.append('file', pdfFile);
            uploadFormData.append('file_name', fileName);
            
            const token = localStorage.getItem('token');
            
            const uploadRes = await fetch('https://vitaldev.vitalinsights.in/user/upload-file-to-s3', {
              method: 'POST',
              body: uploadFormData,
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (!uploadRes.ok) {
              const errorText = await uploadRes.text();
              console.error('Upload error response:', errorText);
              throw new Error(`Upload failed with status ${uploadRes.status}: ${errorText}`);
            }
            
            const response = await uploadRes.json();
            const fileUrl = response.fileUrl || response.file_url || response.url || response.data?.fileUrl || response.data?.file_url;
            
            if (!fileUrl) {
              console.error('No file URL found in response. Full response:', response);
              throw new Error('Invalid response from server - no file URL');
            }
            
            pdf_url = String(fileUrl);
            console.log('Successfully set PDF URL:', pdf_url);
          } catch (uploadError) {
            console.error('Error during PDF upload:', uploadError);
            pdf_url = pdfObjectUrl;
            console.warn('Using object URL as fallback:', pdf_url);
          }
          
          console.log('PDF generated and uploaded:', pdf_url);
        } catch (error) {
          console.error('Error generating PDF:', error);
          setPublishingStatus('error');
          toast.error('Failed to generate PDF');
          setSubmitting(false);
          return;
        }
      }
      
      const payload = {
        ...formData,
        report_status: status,
        appointment_id: appointmentId
      };
      
      if (pdf_url) {
        console.log('Setting PDF URL in submission:', pdf_url);
        payload.pdf_url = pdf_url;
      }

      console.log('About to submit with payload:', JSON.stringify(payload));
      
      const response = await strengthReportService.createReport(payload);
      console.log('Create report response:', response);
      toast.success('Report submitted successfully');

      if (status === 'published') {
        setPublishingStatus('success');
        setTimeout(() => {
          setPublishingStatus(null);
          setSubmitting(false);
          navigate('/patients');
        }, 2000);
      } else {
        setSubmitting(false);
        navigate('/patients');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit report');
      setSubmitting(false);
      if (status === 'published') {
        setPublishingStatus('error');
      }
    }
  };

  const handleCancel = () => {
    navigate('/patients');
  };

  const goToNextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goToPreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderField = (field) => {
    const value = formData[field.name];

    if (field.type === 'textarea') {
      return (
        <div key={field.name} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={value || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="w-full p-2 border rounded"
            rows="4"
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.name} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={value || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.name} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
        <input
          type={field.type}
          value={value || ''}
          onChange={(e) => handleFormChange(field.name, e.target.value)}
          className="w-full p-2 border rounded"
          placeholder={`Enter ${field.label.toLowerCase()}`}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {publishingStatus && (
        <PublishingOverlay status={publishingStatus} />
      )}
      
      <div className="flex flex-col md:flex-row gap-4 p-4">
        <div className="md:w-64 flex-shrink-0">
          <div className="bg-white p-4 rounded-lg shadow space-y-6">
            {/* Patient Information */}
            {patient && (
              <div className="border-b pb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-3">{patient.profile?.name}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID:</span>
                    <span className="font-medium">{patient.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Branch:</span>
                    <span className="font-medium">{patient.sukra_branch_id}</span>
                  </div>
                  {patient.slot_start_time && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Appointment:</span>
                      <span className="font-medium">{patient.slot_start_time?.split("T")[0]}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Report Sections */}
            <div>
              <h3 className="text-xl font-bold text-orange-500 mb-4">Report Sections</h3>
              <ul className="space-y-2">
                {sections.map((section, index) => (
                  <li key={index}>
                    <button
                      onClick={() => setCurrentSection(index)}
                      disabled={submitting}
                      className={`w-full text-left py-2 px-3 rounded transition ${currentSection === index 
                        ? 'bg-orange-500 text-white font-medium' 
                        : 'hover:bg-gray-100'}`}
                    >
                      {section.label}
                      {currentSection === index && (
                        <span className="float-right">→</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        <div className="flex-1">
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold">
                {isEditMode ? 'Edit Strength Report' : 'New Strength Report'}
              </h1>
            </div>
            
            <div className="mb-4">
              <h2 className="text-xl font-semibold">
                {sections[currentSection].label}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Complete all fields in this section before proceeding
              </p>
            </div>

            <div className="space-y-6">
              {sections[currentSection].component === 'upper_body' ? (
                renderExerciseSection('upper_body')
              ) : sections[currentSection].component === 'lower_body' ? (
                renderExerciseSection('lower_body')
              ) : sections[currentSection].component === 'suggestions' ? (
                renderSuggestionsSection()
              ) : (
                sections[currentSection].fields.map(field => renderField(field))
              )}
            </div>

            <div className="flex justify-between mt-6">
              <Button
                variant="secondary"
                onClick={currentSection > 0 ? goToPreviousSection : handleCancel}
              >
                {currentSection > 0 ? 'Previous' : 'Back to List'}
              </Button>

              {currentSection === sections.length - 1 ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handlePreview}
                    disabled={submitting}
                  >
                    Preview Report
                  </Button>
                  <Button
                    onClick={() => handleSubmit('submitted')}
                    disabled={submitting}
                    variant="secondary"
                  >
                    {submitting ? 'Saving...' : 'Submit Report'}
                  </Button>
                  <Button
                    onClick={() => handleSubmit('published')}
                    disabled={submitting}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {submitting ? 'Publishing...' : 'Submit & Publish'}
                  </Button>
                </div>
              ) : (
                <Button onClick={goToNextSection}>Next</Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default StrengthReportForm;
